<script lang="ts">
  import {
    friends, trips, candidates, activeCandidateId,
    togglePlacement, placementMode, clearSavedData, downloadSavedData, importSavedData,
    exampleLoaded, addSheet,
  } from '../lib/state';
  import { hasMapData, loadExampleData, resetExampleData } from '../lib/example';
  import {
    addFriendByAddress, addTripByAddress, setActiveCandidate,
    removeFriend, renameFriend, removeTrip, renameTrip,
  } from '../lib/entities';
  import { user, authReady, signInWithGoogle } from '../lib/auth';
  import type { Prediction, Trip } from '../lib/types';
  import AddressInput from './AddressInput.svelte';
  import Account from './Account.svelte';
  import Chips from './Chips.svelte';
  import CandidateList from './CandidateList.svelte';
  import ModeTabs from './ModeTabs.svelte';
  import CandidateDetail from './CandidateDetail.svelte';
  import Suggestions from './Suggestions.svelte';
  import MapsKeyError from './MapsKeyError.svelte';

  export let keyError = '';
  export let open = false;
  export let onClose: () => void = () => {};

  type View = 'menu' | 'people' | 'places' | 'candidates' | 'candidateDetail' | 'discover' | 'account';
  let view: View = 'menu';

  const TITLES: Record<Exclude<View, 'menu'>, string> = {
    people: 'People',
    places: 'Places',
    candidates: 'Candidates',
    candidateDetail: 'Breakdown',
    discover: 'Ideal neighborhoods',
    account: 'Account & data',
  };

  function go(v: View) { view = v; }
  function back() { view = view === 'candidateDetail' ? 'candidates' : 'menu'; }
  export function openCandidate(id: string) { setActiveCandidate(id); view = 'candidateDetail'; }

  function choosePlacement(mode: 'friend' | 'trip' | 'candidate') {
    togglePlacement(mode);
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) onClose();
  }

  let addValue = '';
  let addRef: AddressInput;
  let backupInput: HTMLInputElement;

  $: activeCand = $candidates.find((c) => c.id === $activeCandidateId) ?? null;

  async function doAdd(prediction: Prediction | null) {
    const v = addValue.trim();
    if (!v) return;
    try {
      const added = view === 'people'
        ? await addFriendByAddress(v, prediction)
        : view === 'places'
          ? await addTripByAddress(v, prediction, { skipPrompt: true })
          : false;
      if (!added) return;
      addRef.reset();
      addValue = '';
    } catch {
      alert('Could not find that address.');
    }
  }

  let aiDialog: HTMLDialogElement;
  let signingIn = false;
  async function doSignIn() {
    if (signingIn) return;
    signingIn = true;
    try {
      await signInWithGoogle();
    } catch {
      alert('Could not sign in. Please try again.');
    } finally {
      signingIn = false;
    }
  }

  async function importBackup(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!confirm('Replace saved data in this browser with this JSON backup?')) return;
    try {
      await importSavedData(file);
    } catch {
      alert('Could not import that JSON backup.');
    }
  }

  function toggleExample() {
    if ($exampleLoaded) {
      if (!confirm('Clear the example? This will clear the current pins.')) return;
      resetExampleData();
      return;
    }
    if (hasMapData() && !confirm('Load the San Francisco example? This will clear your current pins.')) return;
    loadExampleData();
  }

  function promptRenamePlace(place: Trip) {
    const next = prompt('Rename place', place.name)?.trim();
    if (next && next !== place.name) renameTrip(place.id, next);
  }
</script>

<aside class="sidebar" class:open>
  <button class="sidebar-close" on:click={onClose} aria-label="Close menu">×</button>
  <p class="brand"><img class="brand-logo" src="/livenearby-logo.png" alt="livenearby.ai logo" /><span>livenearby.ai</span></p>

  <MapsKeyError message={keyError} />

  {#if view === 'menu'}
    {#if $authReady && !$user}
      <button class="signin-cta" on:click={doSignIn} disabled={signingIn}>
        <span class="signin-cta-badge" aria-hidden="true">
          <svg viewBox="0 0 18 18" width="18" height="18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.88 2.68-6.62z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/></svg>
        </span>
        <span class="signin-cta-text">
          <span class="signin-cta-title">{signingIn ? 'Signing in…' : 'Sign in to save & sync'}</span>
          <span class="signin-cta-sub">Keep your people, places &amp; candidates across devices</span>
        </span>
      </button>
    {/if}
    <h1 class="hero">Live near the people<br />and places you <em>love</em>.</h1>
    <p class="hero-sub">Add friends, coffee shops, gyms, parks, beaches, stadiums, etc. We rate neighborhoods and home/apartment locations based on access to what matters to you.</p>
    {#if $friends.length || $trips.length || $candidates.length}
    <div class="hero-instructions">
      <p>Click and drag pins to move them around.</p>
      <p>Click on different home candidates to see the transportation radius.</p>
    </div>
    {/if}

    {#if $exampleLoaded || (!$friends.length && !$trips.length && !$candidates.length)}
    <button
      class="example-cta"
      class:reset={$exampleLoaded}
      on:click={toggleExample}
      aria-label={$exampleLoaded ? 'Clear the example and its pins' : 'Load the San Francisco example'}
    >
      <span class="example-cta-inner">
        <span class="example-cta-icon" aria-hidden="true">{$exampleLoaded ? '↺' : '✦'}</span>
        <span>
          <span class="example-cta-title">{$exampleLoaded ? 'Start your own' : 'Explore a San Francisco example'}</span>
          <span class="example-cta-sub">{$exampleLoaded ? 'Leave the example and start fresh' : 'See how people, places, and homes fit together'}</span>
        </span>
      </span>
    </button>

    {/if}

    <button class="example-cta" on:click={() => aiDialog.showModal()} aria-haspopup="dialog">
      <span class="example-cta-inner">
        <span class="example-cta-icon" aria-hidden="true">?</span>
        <span class="example-cta-title">FAQ</span>
      </span>
    </button>

    <nav class="panel-menu">
      <button class="menu-row" on:click={() => go('people')}>
        <span class="menu-icon" aria-hidden="true">👥</span>
        <span class="menu-text">
          <span class="menu-label">People</span>
          <span class="menu-sub">{$friends.length} {$friends.length === 1 ? 'person' : 'people'} in your circle</span>
        </span>
        <span class="menu-chev" aria-hidden="true">›</span>
      </button>

      <button class="menu-row" on:click={() => go('places')}>
        <span class="menu-icon" aria-hidden="true">📍</span>
        <span class="menu-text">
          <span class="menu-label">Places</span>
          <span class="menu-sub">{$trips.length} place{$trips.length === 1 ? '' : 's'} that matter to you</span>
        </span>
        <span class="menu-chev" aria-hidden="true">›</span>
      </button>

      <button class="menu-row" on:click={() => go('candidates')}>
        <span class="menu-icon" aria-hidden="true">🏠</span>
        <span class="menu-text">
          <span class="menu-label">Candidates</span>
          <span class="menu-sub">{$candidates.length ? `${$candidates.length} home${$candidates.length === 1 ? '' : 's'} ranked` : 'Add a home to score it'}</span>
        </span>
        <span class="menu-chev" aria-hidden="true">›</span>
      </button>

      <button class="menu-row" on:click={() => go('discover')}>
        <span class="menu-icon" aria-hidden="true">⭐</span>
        <span class="menu-text">
          <span class="menu-label">Ideal neighborhoods</span>
          <span class="menu-sub">Find walkable areas near your people</span>
        </span>
        <span class="menu-chev" aria-hidden="true">›</span>
      </button>

      <button class="menu-row" on:click={() => go('account')}>
        <span class="menu-icon" aria-hidden="true">⚙️</span>
        <span class="menu-text">
          <span class="menu-label">Account &amp; data</span>
          <span class="menu-sub">{$authReady && $user ? ($user.displayName || $user.email) : 'Sign in to sync · backup'}</span>
        </span>
        <span class="menu-chev" aria-hidden="true">›</span>
      </button>
    </nav>
  {:else}
    <div class="panel-head">
      <button class="panel-back" on:click={back}>‹ Back</button>
      <h2 class="view-title">{view === 'candidateDetail' ? (activeCand?.name ?? 'Breakdown') : TITLES[view]}</h2>
    </div>

    {#if view === 'people'}
      <div class="section">
        <div class="section-title">Add your people</div>
        <p class="help-text">Use a rough location. No exact address needed. Drop a pin or search an address, intersection, landmark, or neighborhood.</p>
        <button style="width: 100%;" on:click={() => { onClose(); addSheet.set('friend'); }}>Add friend</button>
      </div>

      <div class="section">
        <div class="section-title">Your people <span class="meta">{$friends.length || ''}</span></div>
        <Chips items={$friends} chipClass="chip-friend" emptyHtml="No friends yet. Add one above" onRename={renameFriend} onRemove={removeFriend} />
      </div>

    {:else if view === 'places'}
      <div class="section">
        <div class="section-title">Add a place</div>
        <div class="add-input-row">
          <AddressInput
            bind:this={addRef}
            bind:value={addValue}
            placeholder="School, cafe, park, or address"
            ariaLabel="Place address to add"
            nameOnly
            on:select={(e) => doAdd(e.detail.prediction)}
            on:submit={() => doAdd(addRef.getPrediction())}
          />
          <button on:click={() => doAdd(addRef.getPrediction())}>Add</button>
        </div>
        <button class="secondary drop-pin-btn" class:active={$placementMode === 'trip'} style="margin-top: 6px; width: 100%;" on:click={() => choosePlacement('trip')}>📍 Place pin on map instead</button>
      </div>

      <div class="section">
        <div class="section-title">Your places <span class="meta">{$trips.length || ''}</span></div>
        {#if $trips.length === 0}
          <div class="empty-state">No places yet. Add a school, cafe, park, or anywhere that matters.</div>
        {:else}
          <div class="place-list">
            {#each $trips as place (place.id)}
              <div class="place-row">
                <button class="place-main" on:click={() => promptRenamePlace(place)} aria-label={`Rename ${place.name}`}>
                  <span class="place-name">{place.name}</span>
                  <span class="place-address">{place.formattedAddress || place.address}</span>
                </button>
                <button class="place-remove" on:click={() => removeTrip(place.id)} aria-label={`Remove ${place.name}`}>×</button>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    {:else if view === 'candidates'}
      <div class="section">
        <div class="section-title">Find a home to compare</div>
        <p class="help-text">Start with a Zillow or Redfin listing, search an address, or drop a pin.</p>
        <button style="width: 100%;" on:click={() => { if (window.matchMedia('(max-width: 768px)').matches) onClose(); addSheet.set('candidate'); }}>Add a home to compare</button>
      </div>

      <div class="section">
        <div class="section-title">Ranked candidates <span class="meta">by NestScore</span></div>
        <CandidateList onSelect={openCandidate} />
      </div>
    {:else if view === 'candidateDetail'}
      <ModeTabs />
      <div id="resultsArea"><CandidateDetail /></div>
    {:else if view === 'discover'}
      <Suggestions />
    {:else if view === 'account'}
      <div class="section"><Account /></div>
      <div class="section">
        <div class="section-title">Saved data</div>
        <div class="button-row">
          <button class="secondary" on:click={downloadSavedData}>Export JSON</button>
          <button class="secondary" on:click={() => backupInput.click()}>Import JSON</button>
        </div>
        <input bind:this={backupInput} type="file" accept="application/json,.json" on:change={importBackup} hidden />
        <p class="help-text" style="margin-top: 8px;">Export a local backup of people, places, candidates, scores, and settings.</p>
        <button class="danger" on:click={clearSavedData} style="width: 100%;">Clear saved data</button>
      </div>
    {/if}
  {/if}
</aside>

<dialog bind:this={aiDialog} class="ai-dialog" aria-labelledby="ai-dialog-title">
  <h2 id="ai-dialog-title">FAQ</h2>
  <p class="ai-question">Q: why does this exist</p>
  <p>A: because i wanted help figuring out where to live with proximity to community, activity, food, nature and other things i like.</p>
  <p class="ai-question">Q: what ai are you running on this site</p>
  <p>A: none, just good old math so far. but i have been using ai to help build it :)</p>
  <p class="ai-question">Q: so what's with the .ai domain</p>
  <p>A: because it was available and it rhymes :)</p>
  <form method="dialog"><button>Close</button></form>
</dialog>

<style>
  .ai-dialog {
    width: min(440px, calc(100vw - 32px));
    max-height: calc(100dvh - 40px);
    padding: 26px;
    background: var(--panel);
    color: var(--ink);
    border: 1px solid var(--line);
    border-radius: 16px;
    box-shadow: 0 18px 60px rgba(20, 31, 25, 0.28);
  }
  .ai-dialog::backdrop { background: rgba(20, 31, 25, 0.42); backdrop-filter: blur(2px); }
  .ai-dialog h2 { margin: 0 0 20px; font: italic 28px/1.1 var(--serif); }
  .ai-dialog p { font-size: 14px; line-height: 1.6; }
  .ai-question { font-weight: 600; }
  .ai-dialog form { display: flex; justify-content: flex-end; margin-top: 22px; }

  .signin-cta {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    background: var(--accent);
    color: #f5edd9;
    border: none;
    border-radius: 12px;
    padding: 14px 16px;
    margin: 4px 0 18px;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(29, 53, 40, 0.22);
    transition: transform 0.08s ease, box-shadow 0.15s ease;
  }
  .signin-cta:hover {
    box-shadow: 0 6px 18px rgba(29, 53, 40, 0.30);
    transform: translateY(-1px);
  }
  .signin-cta:active { transform: translateY(0); }
  .signin-cta:disabled { opacity: 0.7; cursor: default; transform: none; }
  .signin-cta-badge {
    flex: none;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .signin-cta-text { flex: 1; min-width: 0; }
  .signin-cta-title { display: block; font-size: 15px; font-weight: 700; line-height: 1.2; }
  .signin-cta-sub {
    display: block;
    font-size: 11.5px;
    opacity: 0.82;
    margin-top: 3px;
    line-height: 1.3;
  }

  .panel-menu { display: flex; flex-direction: column; margin-top: 6px; }
  .hero-instructions {
    margin: -10px 0 18px;
    color: var(--ink-soft);
    font-size: 11.5px;
    line-height: 1.45;
  }
  .hero-instructions p { margin: 0; }
  .hero-instructions p + p { margin-top: 3px; }
  .example-cta {
    position: relative;
    isolation: isolate;
    width: 100%;
    padding: 2px;
    margin: 0 0 10px;
    overflow: hidden;
    border-radius: 12px;
    background: transparent;
    color: var(--ink);
    text-align: left;
    box-shadow: 0 5px 18px rgba(184, 134, 31, 0.16);
  }
  .example-cta::before {
    content: '';
    position: absolute;
    z-index: -2;
    inset: -150%;
    background: conic-gradient(from 0deg, #b8861f 0 9%, #f8e8ae 14% 44%, #c44536 50% 59%, #f8e8ae 64% 94%, #b8861f 100%);
    animation: example-border-lap 7s linear infinite;
  }
  .example-cta::after {
    content: '';
    position: absolute;
    z-index: -1;
    inset: 2px;
    border-radius: 10px;
    background: #fff8e8;
  }
  .example-cta:hover { color: var(--ink); }
  .example-cta.reset { box-shadow: none; }
  .example-cta-inner { display: flex; align-items: center; gap: 11px; padding: 10px 12px; }
  .example-cta-icon {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    flex: none;
    border-radius: 50%;
    background: #f3dfaa;
    color: #7e5911;
    font-size: 17px;
  }
  .example-cta-title { display: block; font-size: 13.5px; font-weight: 750; line-height: 1.2; }
  .example-cta-sub { display: block; margin-top: 2px; color: var(--ink-soft); font-size: 11px; font-weight: 500; }
  @keyframes example-border-lap { to { transform: rotate(1turn); } }
  @media (prefers-reduced-motion: reduce) { .example-cta::before { animation: none; } }
  .menu-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    background: transparent;
    border: none;
    border-bottom: 1px solid var(--line-soft);
    padding: 16px 4px;
    cursor: pointer;
    color: var(--ink);
    border-radius: 0;
  }
  .menu-row:hover { background: rgba(29, 53, 40, 0.04); }
  .menu-icon { font-size: 20px; flex: none; width: 26px; text-align: center; }
  .menu-text { flex: 1; min-width: 0; }
  .menu-label { display: block; font-size: 14px; font-weight: 600; }
  .menu-sub {
    display: block; font-size: 11.5px; color: var(--ink-soft); margin-top: 2px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .menu-chev { flex: none; color: var(--ink-soft); font-size: 20px; line-height: 1; }

  .place-list { display: flex; flex-direction: column; }
  .place-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 24px;
    gap: 6px;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid var(--line-soft);
  }
  .place-main {
    min-width: 0;
    padding: 0;
    color: var(--ink);
    background: transparent;
    text-align: left;
    border-radius: 0;
  }
  .place-main:hover { color: var(--accent); background: transparent; }
  .place-name {
    display: block;
    overflow: hidden;
    color: inherit;
    font-size: 12.5px;
    font-weight: 650;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .place-address {
    display: block;
    overflow: hidden;
    margin-top: 2px;
    color: var(--ink-soft);
    font-size: 10px;
    font-weight: 400;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .place-remove {
    width: 24px;
    height: 24px;
    padding: 0;
    color: var(--warn);
    background: transparent;
    font-size: 17px;
  }
  .place-remove:hover { background: rgba(184, 68, 58, 0.08); }

  .panel-head { display: flex; flex-direction: column; gap: 2px; margin-bottom: 10px; }
  .panel-back {
    align-self: flex-start;
    background: transparent;
    border: none;
    color: var(--accent);
    font-size: 13px;
    font-weight: 600;
    padding: 2px 0;
    cursor: pointer;
  }
  .panel-back:hover { color: var(--ink); }
  .view-title {
    font-family: var(--serif);
    font-style: italic;
    font-weight: 400;
    font-size: 26px;
    color: var(--ink);
    margin: 2px 0 4px;
    line-height: 1.1;
  }
</style>
